import React, { type ChangeEvent } from "react";
import { Box, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import type { SelectionDataPipeline, SelectionStep } from "@/schemas/routing/selection-pipeline";
import { getStepsForCategory, moveArrayItem } from "@/utils/selection-pipeline";

export interface OrderedCheckboxProps {
	readonly pipeline: SelectionDataPipeline;
	readonly category?: string;
	readonly name: string;
	readonly label: string;
	readonly conflictsWith?: string[];
	readonly updatePipeline?: (pipeline: SelectionDataPipeline) => void;
	readonly handleClick?: (e: ChangeEvent<HTMLInputElement>) => void;
	readonly pendoId: string;
}

export const OrderedCheckbox: React.FC<OrderedCheckboxProps> = ({
	pipeline,
	category,
	name,
	label,
	conflictsWith,
	updatePipeline,
	handleClick,
	pendoId,
}) => {
	const steps = category ? getStepsForCategory(pipeline, category) : [];
	const names = steps.map((i: SelectionStep) => i.name);
	const index = names.indexOf(name);
	const stepConfigured = names.some((step: string) => step === name);
	const hasConflict = !stepConfigured && conflictsWith && names.some((n: string) => conflictsWith.includes(n));

	return (
		<Box display="flex" alignItems="center">
			<FormControlLabel
				sx={{ flex: 1 }}
				control={
					<Checkbox
						checked={stepConfigured}
						disabled={Boolean(hasConflict)}
						onChange={handleClick}
						data-pendoid={pendoId}
					/>
				}
				label={label}
			/>
			{Boolean(stepConfigured) && Boolean(category) && (
				<>
					<IconButton
						disabled={index === names.length - 1}
						onClick={() => {
							moveArrayItem(steps, index, true);
							updatePipeline?.({ ...pipeline });
						}}
						data-testid={`ordered-checkbox__button__move-down-${name}`}
					>
						<KeyboardArrowDownIcon />
					</IconButton>
					<IconButton
						disabled={index <= 0}
						onClick={() => {
							moveArrayItem(steps, index, false);
							updatePipeline?.({ ...pipeline });
						}}
						data-testid={`ordered-checkbox__button__move-up-${name}`}
					>
						<KeyboardArrowUpIcon />
					</IconButton>
				</>
			)}
		</Box>
	);
};
